import { useEffect, useRef, useState } from "react";
import ICONS from "../../../../assets/Icons";
import {
  GlobalConfiguration,
  IntervalConfiguration,
} from "../../../../utils/configurationProvider";
import PrimaryButton from "../../CommonButtons/PrimaryButton";
import PrimaryRightIconFill from "../../CommonButtons/PrimaryRightIconFill";
import TextInputField from "../../CommonInputFields/TextInputField";

const CommonBulkEditModel = ({
  isOpen,
  onClose,
  onConfirm,
  sourceAccountOptions,
  paymentMethodOptions,
  isLoading,
  onSourceAccountClick,
  onLabelsClick,
  selectedLabelsDisplay = "Select labels",
  selectedSourceAccount = "",
  initialPaymentMethod = "",
  initialComment = "",
  selectedLabelsCount = GlobalConfiguration.lengthCheckZero,
}) => {
  const [paymentMethod, setPaymentMethod] = useState(initialPaymentMethod);
  const [comment, setComment] = useState(initialComment);
  const [animate, setAnimate] = useState(false);
  const commentInputRef = useRef(null);

  const initialValuesRef = useRef({
    sourceAccount: "",
    paymentMethod: "",
    comment: "",
  });
  const hasCapturedInitialRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      setPaymentMethod(initialPaymentMethod ?? "");
      setComment(initialComment ?? "");
    } else {
      hasCapturedInitialRef.current = false;
    }
  }, [isOpen, initialPaymentMethod, initialComment]);

  if (isOpen && !hasCapturedInitialRef.current) {
    hasCapturedInitialRef.current = true;
    initialValuesRef.current = {
      sourceAccount: selectedSourceAccount ?? "",
      paymentMethod: initialPaymentMethod ?? "",
      comment: initialComment ?? "",
      labelsCount: selectedLabelsCount ?? GlobalConfiguration.lengthCheckZero,
    };
  }

  const prevCommentRef = useRef(comment);
  useEffect(() => {
    const wasFilled = prevCommentRef.current && prevCommentRef.current !== "";
    prevCommentRef.current = comment;
    if (wasFilled && !comment && commentInputRef.current) {
      commentInputRef.current.focus();
      const id = setTimeout(
        () => commentInputRef.current?.blur(),
        GlobalConfiguration.lengthCheckZero,
      );
      return () => clearTimeout(id);
    }
  }, [comment]);

  if (!isOpen) return null;

  const hasSourceOrPayment =
    (selectedSourceAccount && String(selectedSourceAccount).trim()) ||
    (paymentMethod && String(paymentMethod).trim());

  const initial = initialValuesRef.current;
  const hasChanges =
    String(selectedSourceAccount ?? "") !==
      String(initial.sourceAccount ?? "") ||
    String(paymentMethod ?? "") !== String(initial.paymentMethod ?? "") ||
    String(comment ?? "") !== String(initial.comment ?? "") ||
    Number(selectedLabelsCount ?? GlobalConfiguration.lengthCheckZero) !==
      Number(initial.labelsCount ?? GlobalConfiguration.lengthCheckZero);

  const isConfirmDisabled = !hasChanges || !hasSourceOrPayment || isLoading;

  const handleConfirm = () => {
    if (isConfirmDisabled) return;
    onConfirm({
      sourceAccount: selectedSourceAccount,
      paymentMethod,
      comment,
    });
  };

  return (
    <div
      className="flex flex-col  w-full justify-center items-center m-auto top-1/2 left-1/2 fixed z-z-index-modal p-padding-sm"
      style={{ transform: "translate(-50%, -50%)" }}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className={`  w-full Qmd:w-small-popup-width flex flex-col  gap-gap-md px-padding-md py-padding-md bg-primary-bg rounded-border-radius-md animate-none ${animate ? "Qmd:animate-slideInFromLeft" : "Qmd:animate-slideInFromRight"}`}
      >
        <div className="flex flex-col gap-gap-xs">
          <p className="font-semibold  text-font-md text-text-primary">
            Edit all requests
          </p>
          <span className="font-normal  text-font-sm text-text-secondary">
            Select a source account and add labels or comments. These details
            will be applied to all requests.
          </span>
        </div>
        <div className="flex flex-col gap-gap-xs">
          <p className="font-normal text-font-xs text-text-secondary">
            Source account
          </p>
          <PrimaryRightIconFill
            btnText={selectedSourceAccount || "Select owner account"}
            enabledBgColor="var(--primary-nav-act)"
            isDisabled={false}
            handleFunction={onSourceAccountClick || (() => {})}
            iconToBeAlign="right"
            enabledTextColor={"var(--text-primary)"}
            icon={ICONS?.ARROW_RIGHT_WHITE}
            fullWidthRequired={true}
            hoverOption="white"
            imgColor={"var(--text-primary)"}
          />
        </div>
        {/* <div className="flex flex-col gap-gap-xs">
          <p className="font-normal text-font-xs text-text-secondary">
            Payment method
          </p>
          <SingleSelectDropDownComponent
            options={paymentMethodOptions}
            selected={paymentMethod}
            setSelected={setPaymentMethod}
            isOpen={isPaymentDropdownOpen}
            onOpen={() => setIsPaymentDropdownOpen(true)}
            onClose={() => setIsPaymentDropdownOpen(false)}
            placeholder="Select type"
            searchPlaceholder="Search method"
            showSearch={false}
            type="small"
            disabled={isLoading}
            dropdownType="PaymentMethod"
          />
        </div> */}
        <div className="flex flex-col gap-gap-xs">
          <p className="font-normal text-font-xs text-text-secondary">
            Labels (optional)
          </p>
          <PrimaryRightIconFill
            btnText={selectedLabelsDisplay}
            enabledBgColor="var(--primary-nav-act)"
            isDisabled={isLoading}
            handleFunction={onLabelsClick || (() => {})}
            iconToBeAlign="right"
            enabledTextColor={"var(--text-primary)"}
            icon={ICONS?.ARROW_RIGHT_WHITE}
            fullWidthRequired={true}
            hoverOption="white"
            imgColor={"var(--text-primary)"}
          />
        </div>
        <div>
          <TextInputField
            ref={commentInputRef}
            variant="default"
            placeholder="Comment (optional)"
            value={comment}
            onInput={(e) => setComment(e.target.value)}
            bgColor="var(--primary-bg)"
            fgColor="var(--text-primary)"
            fontKind="var(--font-md)"
            isDisabled={isLoading}
            isFullWidth={true}
          />
        </div>
        <div className="flex justify-end gap-gap-sm ">
          <PrimaryButton
            btnText="Cancel"
            handleFunction={() => {
              if (!isLoading) {
                setAnimate(true);
                setTimeout(() => {
                  onClose();
                }, IntervalConfiguration.intervalSemiMid);
              }
            }}
            isDisabled={isLoading}
            enabledTextColor="var(--text-primary)"
            disabledTextColor="var(--text-primary)"
            outLineColor="var(--text-primary)"
            hoverOption="white"
          />
          <PrimaryButton
            btnText="Update"
            isDisabled={isConfirmDisabled}
            handleFunction={() => {
              if (!isConfirmDisabled) {
                setAnimate(true);
                setTimeout(() => {
                  handleConfirm();
                }, IntervalConfiguration.intervalSemiMid);
              }
            }}
            enabledTextColor="var(--text-primary)"
            disabledTextColor="var(--text-primary)"
            enabledBgColor="var(--positive-act)"
            disabledBgColor="var(--text-disabled)"
            hoverOption="black"
            inActiveVariantNeeded={true}
          />
        </div>
      </div>
    </div>
  );
};

export default CommonBulkEditModel;
